import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { Table, Badge, Button, ConfirmDialog, MediaUploader, unwrapTrpcData, SearchInput } from '@org/ui';
import { trpc, RouterOutputs } from '@org/clients/school';
import { GRAMMAR_GROUPS } from '@org/grammar-content';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getMutationErrorMessage } from '../shared/multi-step-utils';

type VideoRow = RouterOutputs['grammarVideos']['list'][number];

type TopicRow = {
  slug: string;
  number: number;
  title: string;
  groupId: string;
  videoUrl: string | null;
};

const VIDEO_FILE_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

export const GrammarVideosList: React.FC = () => {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { data } = trpc.grammarVideos.list.useQuery();
  const [filterValue, setFilterValue] = React.useState('');
  const [pendingDelete, setPendingDelete] = React.useState<TopicRow | null>(null);

  const videosBySlug = React.useMemo(() => {
    const rows = unwrapTrpcData<VideoRow[]>(data as VideoRow[]) ?? [];
    return new Map(rows.map((row) => [row.topicSlug, row.videoUrl]));
  }, [data]);

  const topics = React.useMemo<TopicRow[]>(() => {
    const result: TopicRow[] = [];
    let number = 0;
    for (const group of GRAMMAR_GROUPS) {
      for (const topic of group.topics) {
        number += 1;
        result.push({
          slug: topic.slug,
          number,
          title: topic.title,
          groupId: group.id,
          videoUrl: videosBySlug.get(topic.slug) ?? null,
        });
      }
    }
    return result;
  }, [videosBySlug]);

  const setMutation = trpc.grammarVideos.set.useMutation({
    onSuccess: async () => {
      await utils.grammarVideos.list.invalidate();
      toast.success(t('grammarVideos.toasts.saveSuccess'));
    },
    onError: (error: unknown) =>
      toast.error(t('grammarVideos.toasts.saveError', { message: getMutationErrorMessage(error) })),
  });

  const removeMutation = trpc.grammarVideos.remove.useMutation({
    onSuccess: async () => {
      await utils.grammarVideos.list.invalidate();
      toast.success(t('grammarVideos.toasts.removeSuccess'));
    },
    onError: (error: unknown) =>
      toast.error(t('grammarVideos.toasts.removeError', { message: getMutationErrorMessage(error) })),
  });

  const confirmDelete = () => {
    if (!pendingDelete) return;
    removeMutation.mutate({ topicSlug: pendingDelete.slug });
    setPendingDelete(null);
  };

  const columnHelper = createColumnHelper<TopicRow>();

  const columns = [
    columnHelper.accessor((row) => row.number, {
      id: 'number',
      header: '#',
      cell: (info) => (
        <span className="text-muted-foreground">{String(info.getValue()).padStart(2, '0')}</span>
      ),
    }),
    columnHelper.accessor((row) => row.title, {
      id: 'title',
      header: t('grammarVideos.columns.topic'),
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),
    columnHelper.accessor((row) => t(`grammarVideos.groups.${row.groupId}`), {
      id: 'group',
      header: t('grammarVideos.columns.group'),
    }),
    columnHelper.accessor((row) => (row.videoUrl ? 'yes' : 'no'), {
      id: 'status',
      header: t('grammarVideos.columns.status'),
      cell: (info) => (
        <Badge variant={info.row.original.videoUrl ? 'outline' : 'secondary'}>
          {info.row.original.videoUrl
            ? t('grammarVideos.status.hasVideo')
            : t('grammarVideos.status.noVideo')}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('grammarVideos.columns.actions'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <MediaUploader
              compact
              acceptedFileTypes={VIDEO_FILE_TYPES}
              title={t('grammarVideos.actions.upload')}
              description={t('grammarVideos.actions.uploadHint')}
              onUploadComplete={(url) => setMutation.mutate({ topicSlug: row.slug, videoUrl: url })}
              onError={(error) =>
                toast.error(t('grammarVideos.toasts.uploadError', { message: error.message }))
              }
            />
            {row.videoUrl ? (
              <>
                <Button variant="outline" size="sm" asChild>
                  <a href={row.videoUrl} target="_blank" rel="noreferrer">
                    {t('grammarVideos.actions.view')}
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-red-600"
                  disabled={removeMutation.isPending}
                  onClick={() => setPendingDelete(row)}
                >
                  {t('grammarVideos.actions.remove')}
                </Button>
              </>
            ) : null}
          </div>
        );
      },
    }),
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('grammarVideos.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('grammarVideos.description')}</p>
      </div>
      <SearchInput
        onChange={setFilterValue}
        placeholder={t('grammarVideos.searchPlaceholder')}
        clearLabel={t('common.actions.clear')}
        className="max-w-sm"
      />
      <Table
        tableData={topics as any}
        columns={columns}
        getRowId={(row: any) => row.slug}
        globalFilter={filterValue}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        description={t('grammarVideos.removeConfirm', { title: pendingDelete?.title ?? '' })}
        confirmLabel={t('grammarVideos.actions.remove')}
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
};
